import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

const SECTIONS = [
  {
    title: "1. What we collect",
    body: `Account info you give us (email, name, profile picture via Google OAuth), videos and other content you upload, DMs and comments you send, payment info processed by Stripe (we never see your full card number), and technical data (IP address, browser type, device info, timestamps, referrer URL). We use cookies and localStorage for session management and preferences.`,
  },
  {
    title: "2. How we use it",
    body: `To operate the Service (transcode your videos, deliver the feed, route DMs), personalize the algorithmic feed, process payments and payouts, detect abuse and fraud, communicate with you about your account, and improve the product. Legal basis (where GDPR applies): contract, legitimate interest, and consent (for optional cookies).`,
  },
  {
    title: "3. AI processing",
    body: `Prompts you submit to the AI Video Generator are sent to OpenAI's Sora 2 API for processing. OpenAI's data usage is governed by their policies. We don't sell your prompts. Generated videos are stored on our infrastructure.`,
  },
  {
    title: "4. Who we share it with",
    body: `Service providers who help us operate (object storage, ffmpeg workers, hosting, Stripe for payments, OpenAI for AI generation, Google for OAuth). We don't sell your personal data. We may disclose data if required by law or to protect our rights or safety.`,
  },
  {
    title: "5. Public content",
    body: `Videos, comments, and profile info you post are public by default. Assume anything you post can be seen by anyone. Direct messages are visible only to sender and recipient (but we store them and may access them for abuse investigation).`,
  },
  {
    title: "6. Cookies",
    body: `We use essential cookies for session management (login, CSRF protection). Analytics cookies are opt-out via your browser. We don't use third-party ad tracking.`,
  },
  {
    title: "7. Retention",
    body: `Account data: as long as you have an active account, plus up to 90 days after deletion for backup/legal purposes. Payment records: 7 years for tax compliance. Uploaded videos: until you delete them or we terminate your account. DMs: until either party deletes them or their account.`,
  },
  {
    title: "8. Your rights",
    body: `Depending on your jurisdiction, you may have the right to access, correct, delete, restrict processing of, or port your personal data, and to object to processing. Email support@matrixvideo.online to exercise these rights. EU/UK users may also lodge a complaint with their data protection authority.`,
  },
  {
    title: "9. Children",
    body: `MatrixShopping is not directed at children under 13. We do not knowingly collect data from children under 13. If you believe a child has provided us data, contact us and we will delete it.`,
  },
  {
    title: "10. Security",
    body: `We use industry-standard encryption (TLS in transit, encryption at rest for sensitive fields). No system is 100% secure, so we can't guarantee absolute security. Report suspected vulnerabilities to support@matrixvideo.online.`,
  },
  {
    title: "11. International transfers",
    body: `Your data may be processed in the United States and other countries. We rely on standard contractual clauses or equivalent safeguards for transfers out of the EU/UK.`,
  },
  {
    title: "12. Changes to this policy",
    body: `We'll notify you of material changes via email or an in-app notice. Continued use after changes means you accept them.`,
  },
  {
    title: "13. Contact",
    body: `Privacy questions or requests: support@matrixvideo.online. General support: support@matrixvideo.online.`,
  },
];

export default function Privacy() {
  const nav = useNavigate();
  return (
    <div className="md:ml-60 min-h-screen pb-24 md:pb-12">
      <div className="max-w-3xl mx-auto px-6 pt-10">
        <button
          data-testid="privacy-back-btn"
          onClick={() => nav(-1)}
          className="text-white/50 hover:text-white text-xs font-mono uppercase tracking-widest flex items-center gap-1.5 mb-6"
        >
          <ArrowLeft className="h-3 w-3" /> back
        </button>
        <header className="mb-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] mb-2">// privacy</p>
          <h1 className="font-display font-black text-4xl md:text-5xl flex items-center gap-3 mb-2">
            <Shield className="h-7 w-7 text-[#00E5FF]" strokeWidth={2.2} />
            Privacy Policy
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            last.updated · feb 2026
          </p>
        </header>
        <div data-testid="privacy-content" className="space-y-6 text-white/70 leading-relaxed text-sm">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="text-white font-display font-black text-lg mb-2">{s.title}</h2>
              <p>{s.body}</p>
            </section>
          ))}
        </div>
        <footer className="mt-14 pt-6 border-t border-white/10 text-white/30 font-mono text-[10px] uppercase tracking-widest">
          Matrix Shopping · operating MatrixShopping · support@matrixvideo.online
        </footer>
      </div>
    </div>
  );
}
